import {gsap} from "../../../libs/gsap/all.js"
import * as PIXI from "pixi.js";
import 'pixi.js/math-extras'
import {nanoid} from 'nanoid'
import {BaseMiniGame} from "./BaseMiniGame";
import {GameACar} from "./components/GameACar";
import {GameABike} from "./components/GameABike";
import {GameAWalker} from "./components/GameAWalker";
import {Bubble} from "./components/Bubble";
import {GameAMainCharacter} from "./components/GameAMainCharacter";
import {TextPopup} from "./components/TextPopup";
import {TextIntro} from "./components/TextIntro";
import {FadeUpSprite} from "./components/FadeUpSprite";
import {Earth} from "./components/Earth";
import {Bird} from "./components/Bird";
import {Cloud} from "./components/Cloud";
import {Tree} from "./components/Tree";
import {GameACrankset} from "./components/GameACrankset";
import {LanguageSwitcher} from "./components/LanguageSwitcher";
import {Howl} from "howler";

export class GameA extends BaseMiniGame {

  // Constructor
  constructor(data) {
    super(data);
    this.appWidth = this.data.appDimensions.width;
    this.appHeight = this.data.appDimensions.height;

    this.mainTimeline = false;
    this.outTimeline = false;
    this.progression = 0;
    this.gameCompleted = false;
    this.gameStarted = false;
    this.currentScreen = 0;

    this.globalAccelerationDuration = 1;
    this.globalAccelerationEasing = "quad.out";
    this.currentSpeed = 0;
    this.targetSpeed = 0;
    this.previousTargetSpeed = 0;
    this.targetSpeedLerp = false;

    this.noProgressTimer = false;
    this.lastProgessTick = 0;
    this.lastSpawnTick = {
      cars: 0,
      bikes: 0,
      walkers: 0
    };

    this.vehicules = {
      cars: {},
      bikes: {},
      walkers: {},
    };

    this.maxX = 0; // this will be set by the background layer size
    this.baseSpeed = 10; // pixels to move each tick when pressed

    this.layersSpeed = {
      "backgroundLayer": 1, // keep this to 1 always as this is the "base" speed
      "cloudsBackgroundLayer": 1.25,
      "photosLayer": 1.025,
      "photosCreditsLayer": 1.025,
      "buildingsLayer": 1.05,
      "treesLayer": 1.1,
      "floorLayer": 1,
      "textLayer": 1,
      "textIntroLayer": 1,
      "bubblesBackgroundLayer": 1.15,
      "bubblesLayer": 1.15,
      "birdLayer": 0.95,
      "foregroundTreesLayer": 1.5,
      "foregroundBubblesLayer": 1.5,
      "foregroundCloudsLayer": 1.25,
    }

    this.bikeSound = new Howl({
      src: ["audio/MAF_Interaction 1.oga"],
      loop: true,
      autoplay: true,
      volume: 0,
    });

    this.completeSound = new Howl({
      src: ["audio/MAF_Themes_Ahhh.oga"],
      loop: false,
      autoplay: false,
      volume: 1,
    });
  }

  async assetLoader() {
    // Game related assets
    if (this.data.medias && this.data.medias.length) {
      for (let i = 0; i < this.data.medias.length; i++) {
        this.assets[this.data.medias[i].name] = await PIXI.Assets.load(this.data.medias[i].url);
      }
    }

    // Global assets
    if (app.data.global.medias && app.data.global.medias.length) {
      for (let i = 0; i < app.data.global.medias.length; i++) {
        this.assets[app.data.global.medias[i].name] = await PIXI.Assets.load(app.data.global.medias[i].url);
      }
    }
  }

  async buildStage() {

    this.stage.eventMode = 'static';

    this.mainContainer = new PIXI.Container();

    this.stage.addChild(this.mainContainer);

    this.buildBackground();
    this.buildOutBackground();
    this.buildCloudsBackgroundLayer();
    this.buildPhotosLayer();
    this.buildBuildingsLayer();
    this.buildTreesLayer();
    this.buildBubblesBackgroundLayer();
    this.buildFloorLayer();

    this.buildBubblesLayer();
    this.buildBirdsLayer();

    this.buildVehiculesLayer();
    this.buildForegroundCloudsLayer();

    this.buildTextIntroLayer();
    this.buildTextPopupLayer();
    this.buildPhotoCreditsLayer();

    this.buildSoundscape();

    this.buildCharacterLayer();
    this.buildForegroundTreesLayer();
    this.buildForegroundBubblesLayer();

    this.buildAppTitle();
    this.buildUI();

    this.buildOverlay();

    this.buildMainTimeline();
    this.buildOutTimeline();

    //add listener
    this.ticker.add(this.progress.bind(this));
  }

  buildBackground() {

    this.backgroundLayer = new PIXI.Container();
    this.backgroundLayer.eventMode = 'none';
    this.stage.addChild(this.backgroundLayer);

    for (let i = 1; i <= 8; i++) {
      let bgPart = new PIXI.Sprite(this.assets["background-" + i]);
      this.backgroundLayer.addChild(bgPart);
      bgPart.position.x = bgPart.width * (i - 1);
    }

    this.maxX = this.backgroundLayer.width;
  }

  buildOutBackground() {

    this.outBackgroundLayer = new PIXI.Container();
    this.outBackgroundLayer.eventMode = 'none';
    this.stage.addChild(this.outBackgroundLayer);

    for (let i = 1; i <= 2; i++) {
      let bgPart = new PIXI.Sprite(this.assets["out-background-" + i]);
      this.outBackgroundLayer.addChild(bgPart);
      bgPart.position.x = bgPart.width * (i - 1);
    }

    this.outBackgroundLayer.position.x = this.appWidth;
  }

  buildBirdsLayer() {

    this.birdLayer = new PIXI.Container();
    this.birdLayer.eventMode = "none";
    this.stage.addChild(this.birdLayer);

    this.birds = [];
    const birds = [
      [690, 466, PIXI.Texture.from("bird-1"), 1.0, this],
      [4430 * this.layersSpeed["birdLayer"], 682, PIXI.Texture.from("bird-2"), 0.95, this],
      [6478 * this.layersSpeed["birdLayer"], 264, PIXI.Texture.from("bird-3"), 0.95, this],
      [10400 * this.layersSpeed["birdLayer"], 282, PIXI.Texture.from("bird-4"), 0.95, this],
      [10630 * this.layersSpeed["birdLayer"], 370, PIXI.Texture.from("bird-5"), 0.95, this],
      [11550 * this.layersSpeed["birdLayer"], 546, PIXI.Texture.from("bird-6"), 0.95, this],
      [11825 * this.layersSpeed["birdLayer"], 200, PIXI.Texture.from("bird-7"), 0.95, this],
      [12425 * this.layersSpeed["birdLayer"], 978, PIXI.Texture.from("bird-8"), 0.95, this],
      [12525 * this.layersSpeed["birdLayer"], 682, PIXI.Texture.from("bird-9"), 0.95, this],
      [14180 * this.layersSpeed["birdLayer"], 666, PIXI.Texture.from("bird-10"), 0.95, this],
      [14550 * this.layersSpeed["birdLayer"], 706, PIXI.Texture.from("bird-11"), 0.95, this],
      [14750 * this.layersSpeed["birdLayer"], 464, PIXI.Texture.from("bird-12"), 0.95, this],
      [15420 * this.layersSpeed["birdLayer"], 275, PIXI.Texture.from("bird-13"), 0.95, this],
      [16700 * this.layersSpeed["birdLayer"], 352, PIXI.Texture.from("bird-14"), 0.95, this],
      [17425 * this.layersSpeed["birdLayer"], 922, PIXI.Texture.from("bird-15"), 0.95, this],
      [17575 * this.layersSpeed["birdLayer"], 724, PIXI.Texture.from("bird-16"), 0.95, this],
      [18000 * this.layersSpeed["birdLayer"], 684, PIXI.Texture.from("bird-17"), 0.95, this],
    ];
    for (let i = 0; i < birds.length; i++) {
      const bird = new Bird(...birds[i]);
      this.birds.push(bird);
      this.birdLayer.addChild(bird);
    }
  }

  buildPhotosLayer() {

    this.photosLayer = new PIXI.Container();
    this.photosLayer.eventMode = 'none';
    this.stage.addChild(this.photosLayer);

    this.photos = [];

    this.photo1 = new FadeUpSprite(3400 * this.layersSpeed["photosLayer"], 500, this.assets["photo-1"], 0.9, this);
    this.photosLayer.addChild(this.photo1);

    this.photo2 = new FadeUpSprite(7055 * this.layersSpeed["photosLayer"], 170, this.assets["photo-2"], 0.9, this);
    this.photosLayer.addChild(this.photo2);

    this.photo3 = new FadeUpSprite(11575 * this.layersSpeed["photosLayer"], 570, this.assets["photo-3"], 0.9, this);
    this.photosLayer.addChild(this.photo3);

    this.photos.push(this.photo1, this.photo2, this.photo3);

    // Let's add the credits layer here so we are at the right zIndex
    this.photosCreditsLayer = new PIXI.Container();
    this.stage.addChild(this.photosCreditsLayer);
  }

  buildBuildingsLayer() {

    this.buildingsLayer = new PIXI.Container();
    this.buildingsLayer.eventMode = 'none';

    let buildingPart1 = new PIXI.Sprite(this.assets["buildings-1"]);
    buildingPart1.position.x = 1208;
    buildingPart1.position.y = 1216;
    this.buildingsLayer.addChild(buildingPart1);

    let buildingPart2 = new PIXI.Sprite(this.assets["buildings-2"]);
    buildingPart2.position.x = 4100 * this.layersSpeed["buildingsLayer"];
    buildingPart2.position.y = 1168;
    this.buildingsLayer.addChild(buildingPart2);

    this.stage.addChild(this.buildingsLayer);
  }

  buildTreesLayer() {

    this.treesLayer = new PIXI.Container();
    this.treesLayer.eventMode = 'none';

    const treePart1 = new PIXI.Sprite(this.assets["trees-1"]);
    treePart1.position.set(546, 1140);
    this.treesLayer.addChild(treePart1);

    const treePart2 = new PIXI.Sprite(this.assets["trees-2"]);
    treePart2.position.set(6458 * this.layersSpeed["treesLayer"], 1131);
    this.treesLayer.addChild(treePart2);

    const treePart3 = new PIXI.Sprite(this.assets["trees-3"]);
    treePart3.position.set(13400 * this.layersSpeed["treesLayer"], 1018);
    this.treesLayer.addChild(treePart3);

    this.stage.addChild(this.treesLayer);
  }

  buildFloorLayer() {

    this.floorLayer = new PIXI.Container();
    this.floorLayer.eventMode = 'none';
    this.stage.addChild(this.floorLayer);

    for (let i = 1; i <= 8; i++) {
      let floorPart = new PIXI.Sprite(this.assets["floor-" + i]);
      this.floorLayer.addChild(floorPart);
      floorPart.position.x = floorPart.width * (i - 1);
    }

    this.maxX = this.floorLayer.width;
  }

  buildTextIntroLayer() {
    // Containers
    this.textIntroLayer = new TextIntro(1, this.data.screens[0], this);
    this.textIntroLayer.eventMode = 'none';
    this.stage.addChild(this.textIntroLayer);
  }

  buildTextPopupLayer() {
    super.buildTextPopupLayer();

    const text1 = new TextPopup((this.appWidth * 1) + (this.appWidth * 0.43), 596, "#343B6F", 0.95, this.data.screens[1], this);
    this.textPopups.push(text1);
    if (text1.getText()) {
      // reduced position using text width
      text1.getText().position.x -= text1.getText().width / 2;
      this.textLayer.addChild(text1.getText());
    }
    if (text1.getPopup()) {
      this.popupLayer.addChild(text1.getPopup());
    }

    const text2 = new TextPopup((this.appWidth * 2) + (this.appWidth * 0.48), 596, "#343B6F", 0.95, this.data.screens[2], this);
    this.textPopups.push(text2);
    if (text2.getText()) {
      // reduced position using text width
      text2.getText().position.x -= text2.getText().width / 2;
      this.textLayer.addChild(text2.getText());
    }
    if (text2.getPopup()) {
      this.popupLayer.addChild(text2.getPopup());
    }

    const text4 = new TextPopup((this.appWidth * 4) + (this.appWidth * 0.1), 596, "#343B6F", 0.95, this.data.screens[4], this);
    this.textPopups.push(text4);
    if (text4.getText()) {
      // reduced position using text width
      text4.getText().position.x -= text4.getText().width / 2;
      this.textLayer.addChild(text4.getText());
    }
    if (text4.getPopup()) {
      this.popupLayer.addChild(text4.getPopup());
    }
  }

  buildCloudsBackgroundLayer() {
    this.cloudsBackgroundLayer = new PIXI.Container();
    this.cloudsBackgroundLayer.eventMode = "none";
    this.stage.addChild(this.cloudsBackgroundLayer);

    this.clouds = [];
    const clouds = [
      [3992 * this.layersSpeed["cloudsBackgroundLayer"], 822, PIXI.Texture.from("cloud-4"), 1, this]
    ];
    for (let i = 0; i < clouds.length; i++) {
      const cloud = new Cloud(...clouds[i]);
      this.clouds.push(cloud);
      this.cloudsBackgroundLayer.addChild(cloud);
    }
  }

  buildForegroundCloudsLayer() {

    this.foregroundCloudsLayer = new PIXI.Container();
    this.foregroundCloudsLayer.eventMode = "none";
    this.stage.addChild(this.foregroundCloudsLayer);

    const clouds = [
      [2550, 822, PIXI.Texture.from("cloud-1"), 1, this],
      [2608 * this.layersSpeed["foregroundCloudsLayer"], 1520, PIXI.Texture.from("cloud-2"), 1, this],
      [3750 * this.layersSpeed["foregroundCloudsLayer"], 1590, PIXI.Texture.from("cloud-3"), 1, this],
      [5100 * this.layersSpeed["foregroundCloudsLayer"], 1624, PIXI.Texture.from("cloud-5"), 1, this],
      [5900 * this.layersSpeed["foregroundCloudsLayer"], 822, PIXI.Texture.from("cloud-6"), 1, this],
      [9100 * this.layersSpeed["foregroundCloudsLayer"], 982, PIXI.Texture.from("cloud-7"), 1, this],
    ];
    for (let i = 0; i < clouds.length; i++) {
      const cloud = new Cloud(...clouds[i]);
      this.clouds.push(cloud);
      this.foregroundCloudsLayer.addChild(cloud);
    }
  }

  buildForegroundTreesLayer() {

    this.foregroundTreesLayer = new PIXI.Container();
    this.foregroundTreesLayer.eventMode = 'none';
    this.stage.addChild(this.foregroundTreesLayer);

    // this.trees = [];
    const trees = [
      [12500 * this.layersSpeed["foregroundTreesLayer"], 938, PIXI.Texture.from("tree-1"), 1, this],
      [16325 * this.layersSpeed["foregroundTreesLayer"], 1824, PIXI.Texture.from("tree-3"), 1, this],
      [16165 * this.layersSpeed["foregroundTreesLayer"], 1922, PIXI.Texture.from("tree-2"), 1, this]
    ];
    for (let i = 0; i < trees.length; i++) {
      const tree = new Tree(...trees[i]);
      // this.trees.push(tree);
      this.foregroundTreesLayer.addChild(tree);
    }
  }

  buildBubblesBackgroundLayer() {
    // Some bubbles are part of the background
    this.bubblesBackgroundLayer = new PIXI.Container();
    this.bubblesBackgroundLayer.eventMode = "none";
    this.stage.addChild(this.bubblesBackgroundLayer);

    this.bubbles = [];
    const bubbles = [
      [12925 * this.layersSpeed["bubblesBackgroundLayer"], 1422, 470, 0x7ABECC, 0.95, this] // blue
    ];
    for (let i = 0; i < bubbles.length; i++) {
      const bubble = new Bubble(...bubbles[i]);
      this.bubbles.push(bubble);
      this.bubblesBackgroundLayer.addChild(bubble);
    }
  }

  buildBubblesLayer() {
    this.bubblesLayer = new PIXI.Container();
    this.bubblesLayer.eventMode = 'none';
    this.stage.addChild(this.bubblesLayer);

    const bubbles = [
      [-260, 770, 532, 0x869E80, 1.0, this], // dark green
      [2246, -510, 748, 0xFF6463, 1.0, this], // red
      [2678, 366, 178, 0xFF995A, 1.0, this], // orange
      [3184, 628, 68, 0xA4CEA2, 1.0, this], // light green
      [3740 * this.layersSpeed["bubblesLayer"], 1036, 295, 0xFF6463, 0.95, this], // red
      [5750 * this.layersSpeed["bubblesLayer"], 898, 52, 0x7ABECC, 0.95, this], // blue
      [6300 * this.layersSpeed["bubblesLayer"], 772, 748, 0xFF6463, 0.95, this], // red
      [8000 * this.layersSpeed["bubblesLayer"], -650, 932, 0xFF995A, 0.95, this], // orange
      [8000 * this.layersSpeed["bubblesLayer"], 382, 52, 0x7ABECC, 0.95, this], // blue
      [11125 * this.layersSpeed["bubblesLayer"], 755, 110, 0x7ABECC, 0.95, this], // blue
      [11125 * this.layersSpeed["bubblesLayer"], 825, 507, 0x7ABECC, 0.95, this], // blue
      [11348 * this.layersSpeed["bubblesLayer"], -616, 748, 0xFF6463, 0.95, this], // red
      [12200 * this.layersSpeed["bubblesLayer"], 408, 86, 0xFF6463, 0.95, this], // red
      [13750 * this.layersSpeed["bubblesLayer"], -374, 596, 0x7ABECC, 0.95, this], // blue
      [14400 * this.layersSpeed["bubblesLayer"], 858, 146, 0x7ABECC, 0.95, this], // blue
      [14550 * this.layersSpeed["bubblesLayer"], 374, 320, 0x7ABECC, 0.95, this], // blue
      [17500 * this.layersSpeed["bubblesLayer"], -1442, 1862, 0x7ABECC, 1.05, this] // blue
    ];
    for (let i = 0; i < bubbles.length; i++) {
      const bubble = new Bubble(...bubbles[i]);
      this.bubbles.push(bubble);
      this.bubblesLayer.addChild(bubble);
    }

    // this.earth = new Earth(17225 * this.layersSpeed["bubblesLayer"], -187, this.assets["earth"], 1, this);
    // this.bubblesLayer.addChild(this.earth);
    this.earth = new Earth(this.appWidth - 1000, -187, this.assets["earth"], 1, this);
    this.stage.addChild(this.earth);
  }

  buildForegroundBubblesLayer() {
    this.foregroundBubblesLayer = new PIXI.Container();
    this.foregroundBubblesLayer.eventMode = 'none';
    this.stage.addChild(this.foregroundBubblesLayer);

    const bubbles = [
      [3178, 2048, 380, 0x869E80, 0.95, this], // green
      [3800 * this.layersSpeed["foregroundBubblesLayer"], 1944, 1862, 0xA4CEA2, 0.95, this], // light green
      [9100 * this.layersSpeed["foregroundBubblesLayer"], 2081, 158, 0xFF6463, 0.95, this], // red
      [12500 * this.layersSpeed["foregroundBubblesLayer"], 1944, 1862, 0xA4CEA2, 0.95, this], // light green
      [15900 * this.layersSpeed["foregroundBubblesLayer"], 1984, 56, 0xFF6463, 0.95, this] // red
    ];

    for (let i = 0; i < bubbles.length; i++) {
      const bubble = new Bubble(...bubbles[i]);
      this.bubbles.push(bubble);
      this.foregroundBubblesLayer.addChild(bubble);
    }
  }

  buildVehiculesLayer() {
    this.vehiculesLayer = new PIXI.Container();
    this.stage.addChild(this.vehiculesLayer);
  }

  buildCharacterLayer() {
    this.characterLayer = new PIXI.Container();
    this.stage.addChild(this.characterLayer);

    this.mainCharacter = new GameAMainCharacter(this);
    this.mainCharacter.x = 724;
    this.mainCharacter.y = 1779;
    this.characterLayer.addChild(this.mainCharacter);
  }


  spawnMissingVehicules(startsHidden, currentTime, type) {

    // if (type !== "cars") {
    let maxFPS = 6; // Limit spawning rate so they do not appear all at the same time
    let frameDuration = 1000 / maxFPS; // Calculate the duration of each frame in milliseconds

    if (currentTime - this.lastSpawnTick[type] < frameDuration) {
      // too soon, skip progress tick
      return;
    }
    // }

    this.lastSpawnTick[type] = currentTime;

    let vehicules = this.vehicules[type];

    const vehiculeOfTypeToAdd = this.data.screens[this.currentScreen]["max_" + type] - Object.keys(vehicules).length;

    if (vehiculeOfTypeToAdd > 0) {
      // console.log("Need to add ", vehiculeOfTypeToAdd, type);
      const randomVehiculeID = nanoid(); // we'll use a random unique id so we can reference the vehicule easily later on

      let vehicule = null;

      if (type == "cars") {
        vehicule = new GameACar(randomVehiculeID, 0, this, startsHidden);
      } else if (type == "bikes") {
        vehicule = new GameABike(randomVehiculeID, 0, this, startsHidden);
      } else if (type == "walkers") {
        vehicule = new GameAWalker(randomVehiculeID, 0, this, startsHidden);
      }

      if (vehicule) {
        this.vehicules[type][randomVehiculeID] = vehicule;
        // vehicule.on("reached-right-limit", this.onVehiculeReachedRightLimit.bind(this));
        // vehicule.on("reached-left-limit", this.onVehiculeReachedLeftLimit.bind(this));
        this.vehiculesLayer.addChild(vehicule);
      }

    }

  }

  spawnAllMissingVehiculesOnScreen() {

    for (const [type, vehicules] of Object.entries(this.vehicules)) {
      const vehiculeOfTypeToAdd = this.data.screens[this.currentScreen]["max_" + type] - Object.keys(vehicules).length;

      if (vehiculeOfTypeToAdd > 0) {
        // console.log("Need to add ", vehiculeOfTypeToAdd, type);
        for (let i = 0; i < vehiculeOfTypeToAdd; i++) {
          const randomVehiculeID = nanoid(); // we'll use a random unique id so we can reference the vehicule easily later on

          let vehicule = null;

          if (type == "cars") {
            vehicule = new GameACar(randomVehiculeID, i, this, false);
          } else if (type == "bikes") {
            vehicule = new GameABike(randomVehiculeID, i, this, false);
          } else if (type == "walkers") {
            vehicule = new GameAWalker(randomVehiculeID, i, this, false);
          }

          if (vehicule) {
            this.vehicules[type][randomVehiculeID] = vehicule;
            // vehicule.on("reached-right-limit", this.onVehiculeReachedRightLimit.bind(this));
            // vehicule.on("reached-left-limit", this.onVehiculeReachedLeftLimit.bind(this));
            this.vehiculesLayer.addChild(vehicule);
          }
        }
      }
    }
  }

  checkVehiculesCount(currentTime, type) {
    // for (const [type, vehicules] of Object.entries(this.vehicules)) {
    let totalActiveVehicules = 0;
    for (const [id, vehicule] of Object.entries(this.vehicules[type])) {
      if (vehicule.taggedForDestroy === false) {
        totalActiveVehicules++;
      }
    }

    const vehiculeOfTypeToRemove = Math.max(0, totalActiveVehicules - this.data.screens[this.currentScreen]["max_" + type]);
    if (vehiculeOfTypeToRemove > 0) {
      // console.log("Need to remove ", vehiculeOfTypeToRemove, type);

      let randomVehiculesIDsToRemove = Object.keys(this.vehicules[type]).sort((a, b) => 0.5 - Math.random()); // randomize array of keys

      randomVehiculesIDsToRemove = randomVehiculesIDsToRemove.slice(0, vehiculeOfTypeToRemove); // select only vehiculeOfTypeToRemove elements

      for (let i = 0; i < randomVehiculesIDsToRemove.length; i++) {
        this.vehicules[type][randomVehiculesIDsToRemove[i]].disable();
        this.vehicules[type][randomVehiculesIDsToRemove[i]].on("disabled", this.onVehiculeDisabled.bind(this));
      }

    }

  }

  removeAllVehicules() {
    for (const [type, vehicules] of Object.entries(this.vehicules)) {
      for (const [id, vehicule] of Object.entries(this.vehicules[type])) {
        this.onVehiculeDisabled({type: type, id: id});
      }

    }
  }

  onVehiculeDisabled(vehicule) {
    this.vehicules[vehicule.type][vehicule.id].off("disabled", this.onVehiculeDisabled.bind(this));
    this.vehicules[vehicule.type][vehicule.id].destroy();
    delete this.vehicules[vehicule.type][vehicule.id];
  }

  buildUI() {

    this.uiLayer = new PIXI.Container();
    this.stage.addChild(this.uiLayer);

    this.crankSet = new GameACrankset(this);
    this.crankSet.x = 3200;
    this.crankSet.y = 1812;
    this.uiLayer.addChild(this.crankSet);

    this.crankSet.on("pointerdown", this.onTouchDown, this);
    this.crankSet.on("pointerup", this.onTouchUp, this);
    this.crankSet.on("pointerupoutside", this.onTouchUp, this);

    // Add language switcher
    this.languageSwitcher = new LanguageSwitcher(this);
    this.stage.addChild(this.languageSwitcher);
  }

  buildMainTimeline() {
    let maxX = -this.backgroundLayer.width + this.appWidth;

    this.mainTimeline = gsap.timeline({
      paused: true,
      defaults: {
        duration: 30,
        ease: "none"
      }
    });

    for (const [name, speed] of Object.entries(this.layersSpeed)) {
      this.mainTimeline.to(this[name], {
        pixi: {
          x: maxX * speed,
        }
      }, 0);
    }

    // Slide down the earth
    this.mainTimeline.fromTo(this.earth, {
      pixi: {
        y: -2000,
      },
    }, {
      pixi: {
        y: -187,
      },
      immediateRender: true,
      ease: "power1.out",
      duration: 8,
    }, "-=8");
  }

  buildOutTimeline() {
    this.outTimeline = gsap.timeline({
      paused: true,
      defaults: {
        duration: 4,
        ease: "linear",
      },
      onComplete: () => {
        this.dispatchEvent(new CustomEvent("complete", {detail: "GameA Completed."}))
      },
      onStart: () => {
        this.completeSound.seek(0).play();
      },
    });

    let slideDistance = 2000;

    for (const [name, speed] of Object.entries(this.layersSpeed)) {
      this.outTimeline.to(this[name], {
        pixi: {
          x: "-=" + (slideDistance * speed), // scroll 2/3 of the screen
        }
      }, 0);
    }

    this.outTimeline.to(this.outBackgroundLayer, {
      pixi: {
        x: "-=" + slideDistance, // arbitrary number that worked based on above transition
      }
    }, 0);

    this.outTimeline.fromTo(this.fadeOverlayLayer, {
      pixi: {
        alpha: 0,
      }
    }, {
      pixi: {
        alpha: 1,
      },
      duration: 2,
      overwrite: "auto"
    }, 2)
  }

  activate() {
    // Enable render
    this.start();
    this.ticker.start();
    this.soundscape.start();

    // TODO: start the timelines here so they are moving while fading in view
  }

  deactivate() {
    // Disable render
    this.stop();
    this.ticker.stop();

    this.disableUI();
    this.languageSwitcher.disable();

    // Stop sound
    this.bikeSound.fade(this.bikeSound.volume(), 0, 150)
    this.soundscape.stopAll()

    // TODO: Pause all the timelines so the game isn't doing a thing while not on screen
  }

  begin() {
    // Game is ready to go, on screen, and animations have started
    // TODO: enable actions here and make arrow appear if no action are performed after X seconds
    this.startNoActionTimer();

    this.enableUI();
    this.languageSwitcher.enable();

    this.fadeOutOverlay();
  }

  progress(ticker) {
    for (const [type, vehicules] of Object.entries(this.vehicules)) {
      // this.spawnMissingVehicules(true, ticker.lastTime, type);
      this.spawnMissingVehicules(false, ticker.lastTime, type);
    }

    for (const [type, vehicules] of Object.entries(this.vehicules)) {
      this.checkVehiculesCount(ticker.lastTime, type);
    }

    this.moveVehicules(this.isRunning, ticker.deltaTime);

    if (this.gameCompleted) {
      return;
    }

    // Handle progression
    if (this.isRunning) {
      this.targetSpeed = this.baseSpeed;
    } else {
      this.targetSpeed = 0;
    }


    if (this.targetSpeed != this.previousTargetSpeed) {

      if (this.targetSpeedLerp !== false) {
        this.targetSpeedLerp.kill();
        this.targetSpeedLerp = false;
      }

      this.targetSpeedLerp = gsap.to(this,
        {
          currentSpeed: this.targetSpeed,
          duration: this.globalAccelerationDuration,
          ease: this.globalAccelerationEasing,
          overwrite: "auto"
        }
      );

      this.previousTargetSpeed = this.targetSpeed;
    }

    // Will technically only progress if speed is greater than 0
    this.progression += ((this.currentSpeed / this.maxX) * ticker.deltaTime);
    this.progression = Math.min(this.progression, 1);
    super.progress(this.progression);

    this.mainTimeline.progress(this.progression);

    this.currentScreen = Math.min(this.data.screens.length - 1, Math.floor(this.data.screens.length * this.progression));

    if (this.mainTimeline.progress() >= 1) {
      // reached the end, let's transition to the end and complete
      // console.log("game completed");
      this.gameCompleted = true;
      this.disableUI();
      this.languageSwitcher.disable();
      for (let i = 0; i < this.textPopups.length; i++) {
        this.textPopups[i].disable();
      }

      if (this.targetSpeedLerp !== false) {
        this.targetSpeedLerp.kill();
        this.targetSpeedLerp = false;
      }

      this.onTouchDown(); // fake continue the crank and main character animation
      this.outTimeline.play();
    }

    this.lastProgessTick = ticker.lastTime;
  }

  moveVehicules(backgroundMoving, deltaTime) {

    for (const [type, vehicules] of Object.entries(this.vehicules)) {
      for (const [id, vehicule] of Object.entries(vehicules)) {
        vehicule.move(backgroundMoving, deltaTime);
      }
    }
  }

  enableUI() {
    this.crankSet.enable();
  }

  disableUI() {
    this.crankSet.disable();
  }

  startNoActionTimer() {
    this.stopNoActionTimer();

    this.noProgressTimer = setTimeout(() => {
      this.crankSet.startIdleMode();
    }, 5000)
  }

  stopNoActionTimer() {
    if (this.noProgressTimer !== false) {
      clearTimeout(this.noProgressTimer);
      this.noProgressTimer = false;
    }
  }

  onTouchDown(e) {
    this.mainCharacter.animateIn();
    this.crankSet.onPress();

    this.crankSet.stopIdleMode();
    this.stopNoActionTimer();

    this.isRunning = true;

    // Intro, switch text when user start moving in timeline
    if (!this.gameStarted) {
      this.textIntroLayer.nextStep();
    }

    this.bikeSound.fade(this.bikeSound.volume(), 1, 350)

    this.gameStarted = true;
  }

  onTouchUp(e) {
    this.mainCharacter.animateOut();
    this.crankSet.onRelease();


    this.bikeSound.fade(this.bikeSound.volume(), 0, 350)

    // If you want to show the idle arrow again, uncomment this
    // this.startNoActionTimer();
    this.isRunning = false;
  }

  reset() {
    this.gameStarted = false;
    this.gameCompleted = false;
    this.isRunning = false;

    this.currentScreen = 0;

    this.lastProgessTick = 0;
    this.lastSpawnTick = {
      cars: 0,
      bikes: 0,
      walkers: 0
    };
    this.progression = 0;

    this.outTimeline.progress(0, true);
    this.mainTimeline.progress(0, true);

    this.outTimeline.kill();
    this.buildOutTimeline();

    this.currentSpeed = 0;
    this.targetSpeed = 0;
    this.previousTargetSpeed = 0;

    if (this.targetSpeedLerp !== false) {
      this.targetSpeedLerp.kill();
      this.targetSpeedLerp = false;
    }

    this.stopNoActionTimer();

    // Reset intro
    this.textIntroLayer.resetSteps();
    this.textIntroLayer.reset();

    // Reset birds
    for (let i = 0; i < this.birds.length; i++) {
      this.birds[i].reset();
    }

    // Reset photos
    for (let i = 0; i < this.photos.length; i++) {
      this.photos[i].reset();
    }

    // Reset bubbles
    for (let i = 0; i < this.bubbles.length; i++) {
      this.bubbles[i].reset();
    }

    // Reset text popup
    for (let i = 0; i < this.textPopups.length; i++) {
      this.textPopups[i].hidePopup();
      this.textPopups[i].reset();
    }

    // Reset clouds
    for (let i = 0; i < this.clouds.length; i++) {
      this.clouds[i].reset();
    }

    // Reset vehicules
    this.removeAllVehicules();
    this.spawnAllMissingVehiculesOnScreen();

    // reset button
    this.crankSet.reset();
    this.onTouchUp();

    this.fadeOverlayLayer.alpha = 1;

  }
}
